import type { Dispatch, PropsWithChildren, ReactElement } from 'react';
import { useReducer, createContext, useContext, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import appReducer, { type AppState as AppState, type AppStateAction } from './reducers/appReducer';
import { GET_EVENT, type GetEventData, type GetEventVariables } from './graphql/getEvent';
import fontColorContrast from 'font-color-contrast';
import { AppLayoutSkeleton } from '@fastnorth/kraken/components/page-layout';
import {
  GET_EVENT_GALLERY_SAMPLE,
  type GetEventGallerySampleData,
  type GetEventGallerySampleVariables,
} from '../views/leaderboard/components/details/hooks/graphql/getEventGallery';
import GeneralError from '../views/error/GeneralError.tsx';
import Empty from '@fastnorth/kraken/components/empty';
import { useTranslation } from 'react-i18next';
import logDebugMessage from '../utils/logDebugMessage.ts';
import isResultsUrlExternal from './utils/isResultsUrlExternal';
import RedirectToExternalResultsUrl from './components/RedirectToExternalResultsUrl.tsx';

export const defaultState: AppState = {
  event: undefined,
  hasRacesWithPredictiveTracking: false,
  participantSearchSheetState: false,
};

type AppProviderCtx = [AppState, Dispatch<AppStateAction>];

const AppCtx = createContext<AppProviderCtx>([defaultState, () => null]);

const AppProvider = ({ children }: PropsWithChildren): ReactElement => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.components.provider' });
  const [state, dispatch] = useReducer(appReducer, defaultState);
  const { eventUniqueCode = '' } = useParams();

  const [loadEvent, { loading, error, data, called }] = useLazyQuery<
    GetEventData,
    GetEventVariables
  >(GET_EVENT, {
    fetchPolicy: 'cache-and-network',
  });

  const [loadGallery] = useLazyQuery<GetEventGallerySampleData, GetEventGallerySampleVariables>(
    GET_EVENT_GALLERY_SAMPLE,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    if (eventUniqueCode) {
      loadEvent({ variables: { eventUniqueCode } }).then(({ data }) => {
        if (data?.getEvent) {
          dispatch({ type: 'setEvent', payload: data.getEvent });
          if (data?.getEvent?.gallery?.id) {
            loadGallery({ variables: { galleryId: data.getEvent.gallery?.id as string } }).then(
              (photoResults) => {
                if (photoResults.data?.getEventGallerySample?.photos?.length > 0) {
                  dispatch({
                    type: 'setSamplePhotos',
                    payload: photoResults.data?.getEventGallerySample.photos ?? [],
                  });
                }
              }
            );
          }
        }
      });
    }
  }, [eventUniqueCode, loadEvent, loadGallery]);

  useEffect(() => {
    //do we have branding colors?
    const brandingEnabled = data?.getEvent?.applyEventBranding === true;
    const brandingColors = data?.getEvent?.styles?.colors;
    if (brandingEnabled && brandingColors) {
      dispatch({
        type: 'setBrandingColors',
        payload: {
          primary: brandingColors.primary,
          'primary-foreground': brandingColors.primaryFont,
          secondary: brandingColors.secondary,
          'secondary-foreground': brandingColors.secondaryFont,
          tertiary: brandingColors.tertiary,
          'tertiary-foreground': brandingColors.tertiaryFont,
          'branded-background': brandingColors.background,
          'branded-foreground': fontColorContrast(brandingColors.background),
        },
      });
    }
  }, [data?.getEvent?.styles?.colors, data?.getEvent?.applyEventBranding]);

  if (error) {
    logDebugMessage('[AppProvider] rendering error');
    return (
      <GeneralError
        id={'app-provider-error'}
        title={t('error_state.title', { itemName: 'event' })}
        message={t('error_state.message', { itemName: 'event' })}
        error={error}
      />
    );
  }

  if (!called || (loading && !data)) {
    logDebugMessage('[AppProvider] rendering skeleton');
    //show loading
    return <AppLayoutSkeleton />;
  }

  if (!loading && called && (!data || !data.getEvent)) {
    logDebugMessage('[AppProvider] rendering empty state');
    return (
      <Empty
        id={'app-provider-empty-state'}
        heading={t('empty_state.heading', { itemName: 'event' })}
        description={t('empty_state.description')}
        image={{
          src: 'https://cdn.raceroster.com/assets/cerbicons/empty-race-results.svg',
          alt: t('empty_state.image_alt_text', { itemName: 'event' }),
          variant: 'illustration',
        }}
      />
    );
  }

  if (data?.getEvent?.resultsUrl && isResultsUrlExternal(data?.getEvent?.resultsUrl)) {
    logDebugMessage('[AppProvider] showing external results intercept');
    return <RedirectToExternalResultsUrl resultsUrl={data?.getEvent?.resultsUrl} />;
  }

  logDebugMessage('[AppProvider] rendering children');
  return <AppCtx.Provider value={[state, dispatch]}>{children}</AppCtx.Provider>;
};

export const useApp = (): AppProviderCtx => useContext<AppProviderCtx>(AppCtx);

export default AppProvider;
